import { z } from "zod";

import { uuidSchema } from "./primitives.js";

/**
 * 首页内容。
 *
 * 首页上「哪些是可以改的」有一条明确的线：
 *
 * - **可改**：主视觉轮播、定制工坊的步骤、四个卖点、下单后的制作流程步骤、推荐哪些作品。
 *   这些是运营要换的东西（换季、换主推款），放在后台。
 * - **固定**：区块标题这类文案（「定制工坊」「驻场设计师」）写在页面里。
 *   把它们也做成可配置，只会让后台多出一堆没人改的输入框。
 *
 * 所有图片都用相对路径（与目录照片同一套规则），客户端与服务地址拼接后使用。
 */

export const homeSlideSchema = z.object({
  id: uuidSchema,
  /** 主视觉大图，竖图。 */
  imagePath: z.string().min(1),
  /** 图上的小标签，例如「+ 天然灵感」。 */
  tag: z.string().max(20),
  title: z.string().min(1).max(40),
  subtitle: z.string().max(120),
});

export type HomeSlidePayload = z.infer<typeof homeSlideSchema>;

/** 首页上的步骤行：定制工坊的四步、四个卖点、下单后的制作流程，共用这一种结构。 */
export const homeStepSchema = z.object({
  id: uuidSchema,
  /** workshop＝定制工坊步骤，highlight＝卖点，crafting＝下单后制作流程。 */
  kind: z.enum(["workshop", "highlight", "crafting"]),
  /** 步骤号或序号，例如「STEP 01」「01」。复用在流程类区块。 */
  label: z.string().max(20),
  title: z.string().min(1).max(30),
  subtitle: z.string().max(120),
});

export type HomeStepPayload = z.infer<typeof homeStepSchema>;

/** 首页要的全部内容。推荐作品与设计师由各自的接口给，不塞进这里。 */
export const homeContentSchema = z.object({
  slides: z.array(homeSlideSchema),
  workshopSteps: z.array(homeStepSchema),
  highlights: z.array(homeStepSchema),
  craftingSteps: z.array(homeStepSchema),
});

export type HomeContentPayload = z.infer<typeof homeContentSchema>;
